import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import Swal from 'sweetalert2';

const AdminManagement = () => {
    const { api, showToast, user: currentUser } = useAuth();
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [formData, setFormData] = useState({
        id: null,
        username: '',
        password: '',
        fullname: '',
        email: '',
        role: 'admin'
    });
    
    useEffect(() => {
        loadAdmins();
    }, []);
    
    const loadAdmins = async () => {
        try {
            setLoading(true);
            const response = await api.admin.getAll();
            if (response.success) {
                setAdmins(response.data);
            }
        } catch (error) {
            console.error('Error loading admins:', error);
            showToast('เกิดข้อผิดพลาดในการโหลดข้อมูล', 'error');
        } finally {
            setLoading(false);
        }
    };
    
    const handleInputChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };
    
    const handleAdd = () => {
        setEditMode(false);
        setFormData({
            id: null,
            username: '',
            password: '',
            fullname: '',
            email: '',
            role: 'admin'
        });
        setShowModal(true);
    };
    
    const handleEdit = (admin) => {
        setEditMode(true);
        setFormData({
            id: admin.id,
            username: admin.username,
            password: '',
            fullname: admin.fullname,
            email: admin.email || '',
            role: admin.role
        });
        setShowModal(true);
    };
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        try {
            let response;
            if (editMode) {
                const updateData = {
                    fullname: formData.fullname,
                    email: formData.email,
                    role: formData.role
                };
                if (formData.password) {
                    updateData.password = formData.password;
                }
                response = await api.admin.update(formData.id, updateData);
            } else {
                response = await api.admin.create(formData);
            }
            
            if (response.success) {
                showToast(editMode ? 'อัปเดตข้อมูลเรียบร้อยแล้ว' : 'เพิ่มผู้ดูแลระบบเรียบร้อยแล้ว');
                setShowModal(false);
                loadAdmins();
            }
        } catch (error) {
            const message = error.response?.data?.message || 'เกิดข้อผิดพลาด';
            showToast(message, 'error');
        }
    };
    
    const handleDelete = async (id) => {
        if (id === currentUser.id) {
            showToast('ไม่สามารถลบบัญชีของตนเองได้', 'warning');
            return;
        }
        
        const result = await Swal.fire({
            icon: 'warning',
            title: 'ยืนยันการลบ',
            text: 'คุณแน่ใจหรือไม่ที่จะลบผู้ดูแลระบบนี้?',
            showCancelButton: true,
            confirmButtonText: 'ใช่, ลบเลย',
            cancelButtonText: 'ยกเลิก',
            confirmButtonColor: '#dc3545'
        });
        
        if (result.isConfirmed) {
            try {
                const response = await api.admin.delete(id);
                if (response.success) {
                    showToast('ลบผู้ดูแลระบบเรียบร้อยแล้ว');
                    loadAdmins();
                }
            } catch (error) {
                showToast('เกิดข้อผิดพลาดในการลบข้อมูล', 'error');
            }
        }
    };
    
    return (
        <div className="admin-management py-4">
            <div className="container-fluid">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h1 className="h3 mb-0">
                        <i className="fas fa-users me-2"></i>
                        จัดการผู้ดูแลระบบ
                    </h1>
                    <button className="btn btn-gradient-primary" onClick={handleAdd}>
                        <i className="fas fa-plus me-2"></i>
                        เพิ่มผู้ดูแลระบบ
                    </button>
                </div>
                
                {/* Table */}
                <div className="card">
                    <div className="card-header card-header-gradient">
                        <h5 className="mb-0">รายชื่อผู้ดูแลระบบ</h5>
                    </div>
                    <div className="card-body">
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-hover">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>ชื่อผู้ใช้</th>
                                            <th>ชื่อ-นามสกุล</th>
                                            <th>อีเมล</th>
                                            <th>สิทธิ์</th>
                                            <th>วันที่สร้าง</th>
                                            <th>การจัดการ</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {admins.map((admin, index) => (
                                            <tr key={admin.id}>
                                                <td>{index + 1}</td>
                                                <td>
                                                    {admin.username}
                                                    {admin.id === currentUser.id && (
                                                        <span className="badge bg-info ms-2">คุณ</span>
                                                    )}
                                                </td>
                                                <td>{admin.fullname}</td>
                                                <td>{admin.email || '-'}</td>
                                                <td>
                                                    <span className={`badge ${
                                                        admin.role === 'super_admin' 
                                                            ? 'bg-danger' 
                                                            : 'bg-primary'
                                                    }`}>
                                                        {admin.role === 'super_admin' ? 'Super Admin' : 'Admin'}
                                                    </span>
                                                </td>
                                                <td>
                                                    {new Date(admin.created_at).toLocaleDateString('th-TH')}
                                                </td>
                                                <td>
                                                    <div className="btn-group btn-group-sm">
                                                        <button className="btn btn-warning"
                                                                title="แก้ไข"
                                                                onClick={() => handleEdit(admin)}>
                                                            <i className="fas fa-edit"></i>
                                                        </button>
                                                        <button className="btn btn-danger"
                                                                title="ลบ"
                                                                onClick={() => handleDelete(admin.id)}
                                                                disabled={admin.id === currentUser.id}>
                                                            <i className="fas fa-trash"></i>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                        {admins.length === 0 && (
                                            <tr>
                                                <td colSpan="7" className="text-center text-muted py-4">
                                                    ไม่พบข้อมูล
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
                
                {/* Add/Edit Modal */}
                {showModal && (
                    <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog">
                            <div className="modal-content">
                                <form onSubmit={handleSubmit}>
                                    <div className="modal-header">
                                        <h5 className="modal-title">
                                            <i className={`fas fa-${editMode ? 'edit' : 'plus'} me-2`}></i>
                                            {editMode ? 'แก้ไขข้อมูลผู้ดูแลระบบ' : 'เพิ่มผู้ดูแลระบบ'}
                                        </h5>
                                        <button type="button" 
                                                className="btn-close"
                                                onClick={() => setShowModal(false)}></button>
                                    </div>
                                    <div className="modal-body">
                                        <div className="mb-3">
                                            <label className="form-label">
                                                ชื่อผู้ใช้ <span className="text-danger">*</span>
                                            </label>
                                            <input type="text" 
                                                   className="form-control" 
                                                   name="username"
                                                   value={formData.username}
                                                   onChange={handleInputChange}
                                                   disabled={editMode}
                                                   required />
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label">
                                                รหัสผ่าน {!editMode && <span className="text-danger">*</span>}
                                            </label>
                                            <input type="password" 
                                                   className="form-control" 
                                                   name="password"
                                                   value={formData.password}
                                                   onChange={handleInputChange}
                                                   placeholder={editMode ? 'ปล่อยว่างหากไม่ต้องการเปลี่ยน' : ''}
                                                   required={!editMode} />
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label">
                                                ชื่อ-นามสกุล <span className="text-danger">*</span>
                                            </label>
                                            <input type="text" 
                                                   className="form-control" 
                                                   name="fullname"
                                                   value={formData.fullname}
                                                   onChange={handleInputChange}
                                                   required />
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label">อีเมล</label>
                                            <input type="email" 
                                                   className="form-control" 
                                                   name="email"
                                                   value={formData.email}
                                                   onChange={handleInputChange} />
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label">สิทธิ์</label>
                                            <select className="form-select"
                                                    name="role"
                                                    value={formData.role}
                                                    onChange={handleInputChange}>
                                                <option value="admin">Admin</option>
                                                <option value="super_admin">Super Admin</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="modal-footer">
                                        <button type="button" 
                                                className="btn btn-secondary"
                                                onClick={() => setShowModal(false)}>
                                            ยกเลิก
                                        </button>
                                        <button type="submit" className="btn btn-gradient-primary">
                                            <i className="fas fa-save me-2"></i>
                                            บันทึก
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminManagement;